/**
 * ч.849 (рішення власника, новий дизайн сторінок документів) — ПРАВА КОЛОНКА:
 *  1. «Заповнення документа» — прогрес по блоках, рахує ЛИШЕ ОБОВʼЯЗКОВІ поля
 *     (уточнення власника) + рядок «готово до проведення».
 *  2. «Повʼязані дії» — те, що раніше жило у кнопці «ще дії ▾» на плашці
 *     (рішення власника: вправо йде САМЕ «ще дії», решта лишається внизу).
 *  3. «Історія документа» — 2 останні записи + «ще» → наявний попап.
 *  4. «Звʼязані документи» — 2 останні + «ще» → наявний попап.
 */
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { ExtraAction } from './DocumentToolbar';
import type { DtKtDocRef } from './DtKtJournal';
import { HistoryModal } from './DocHistory';
import { RecordOwnerCard } from './RecordOwner';
import {
  RelatedDocsButton, relatedDocName, relatedDocRoute, relatedKeys, relationLabel, useRelatedDocs,
} from './RelatedDocs';
import { LinkDocumentButton } from './DocLinks';
import { blockDone, type DocBlockState } from './DocBlock';

const fmtDT = (v: string) => new Date(v).toLocaleString('uk-UA', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
});
const fmtD = (v: string | null) => (v ? new Date(v).toLocaleDateString('uk-UA') : '—');

const ACTION_LABEL: Record<string, string> = {
  CREATE: 'створено', UPDATE: 'змінено', DELETE: 'видалено',
  POST: 'проведено', UNPOST: 'розпроведено',
};

export default function DocRail({ blocks, actions, doc, isNew, related = true, extraCards }: {
  /** Блоки редактора — для прогресу заповнення обовʼязкових полів. */
  blocks?: DocBlockState[];
  /** «Повʼязані дії» (колишні extraActions кнопки «ще дії»). */
  actions?: ExtraAction[];
  /** Документ для історії та звʼязаних. */
  doc?: DtKtDocRef;
  isNew?: boolean;
  /** ч.885: довідники — без картки «Звʼязані документи» (лише історія). */
  related?: boolean;
  /** ч.937: власна картка правої колонки (напр. документи договору). */
  extraCards?: ReactNode;
}) {
  return (
    <aside className="doclayout__rail">
      {blocks && blocks.length > 0 && <FillProgress blocks={blocks} />}
      {actions && actions.length > 0 && <RelatedActions actions={actions} />}
      {extraCards}
      {/* ч.850 (рішення власника): звʼязані документи — ПЕРЕД історією.
          ч.888 (зауваження власника): картки видно ЗАВЖДИ — на новому
          (незбереженому) документі з текстом «зʼявиться після збереження». */}
      {related && (doc && !isNew
        ? <RelatedCard doc={doc} />
        : <PendingCard title="Звʼязані документи" testId="doc-related-card" />)}
      {/* ч.1313: відповідальний за документом — перед історією. */}
      {doc && !isNew && <RecordOwnerCard doc={doc} />}
      {doc && !isNew
        ? <HistoryCard doc={doc} />
        : <PendingCard title="Історія документа" testId="doc-history-card" />}
    </aside>
  );
}

/** Прогрес — лише ОБОВʼЯЗКОВІ поля (рішення власника ч.849). */
function FillProgress({ blocks }: { blocks: DocBlockState[] }) {
  const totalReq = blocks.reduce((s, b) => s + (b.required ?? 0), 0);
  const totalFilled = blocks.reduce((s, b) => s + Math.min(b.filled ?? 0, b.required ?? 0), 0);
  const pct = totalReq === 0 ? 100 : Math.round((totalFilled / totalReq) * 100);
  const doneBlocks = blocks.filter(blockDone).length;
  const ready = totalFilled >= totalReq;
  return (
    <div className="docrail__card" data-testid="doc-fill-progress">
      <div className="docrail__title">
        Заповнення документа
        <small>{doneBlocks} з {blocks.length} блоків</small>
      </div>
      <div className="docrail__bar" style={{ marginBottom: 8 }}>
        <div style={{ width: `${pct}%`, background: ready ? '#22c55e' : '#3b82f6' }} />
      </div>
      {blocks.map((b, i) => {
        const done = blockDone(b);
        return (
          <div key={b.id} className="docrail__step">
            <span className={`docrail__dot${done ? ' docrail__dot--done' : ''}`}>{done ? '✓' : ''}</span>
            <span style={{ color: done ? undefined : 'var(--muted)' }}>{i + 1}. {b.title}</span>
          </div>
        );
      })}
      <div style={{ marginTop: 8, fontSize: '0.8rem', color: ready ? '#15803d' : 'var(--muted)' }}>
        {ready
          ? 'Готово до проведення — заповнені всі обовʼязкові поля.'
          : `Заповніть обовʼязкові поля (${totalFilled} з ${totalReq}).`}
      </div>
    </div>
  );
}

/** ч.852: іконка документа у рядку дії (як на макеті власника). */
function RailDocIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden
      style={{ flex: '0 0 auto' }}>
      <path d="M9.5 1.5H4a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V5l-3.5-3.5Z"
        stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
      <path d="M9.5 1.5V5H13" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
}

/** «Повʼязані дії» — колишня кнопка «ще дії ▾». */
function RelatedActions({ actions }: { actions: ExtraAction[] }) {
  return (
    <div className="docrail__card" data-testid="doc-related-actions">
      <div className="docrail__title">Повʼязані дії</div>
      {actions.map((a, i) => (
        <button key={i} type="button" className="docrail__link docrail__action"
          disabled={a.disabled} title={a.title}
          style={a.danger ? { color: '#dc2626' } : undefined}
          onClick={a.onClick}>
          <RailDocIcon />
          <span style={{ flex: 1 }}>{a.label}</span>
          <span aria-hidden style={{ color: '#94a3b8' }}>›</span>
        </button>
      ))}
    </div>
  );
}

/** ч.888: заглушка картки для незбереженого документа. */
function PendingCard({ title, testId }: { title: string; testId: string }) {
  return (
    <div className="docrail__card" data-testid={testId}>
      <div className="docrail__title">{title}</div>
      <div style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>Зʼявиться після збереження документа.</div>
    </div>
  );
}

/** ч.1423: сервер віддає записи від ранніх до пізніх, картка показує два останні, свіжий першим. */
export function latestTwo<T>(items: T[]): T[] {
  return items.slice(-2).reverse();
}

/** Історія: 2 останні записи + «ще» → наявний попап. */
function HistoryCard({ doc }: { doc: DtKtDocRef }) {
  const [open, setOpen] = useState(false);
  const docId = doc.id && doc.id !== 'new' ? doc.id : undefined;
  const q = useQuery({
    queryKey: ['doc-history', doc.type, docId],
    queryFn: () => api.get<{ items: { occurred_at: string; user_email: string | null; action: string }[] }>(
      '/documents/audit-history',
      { params: { document_type: doc.type, document_id: docId } },
    ).then((r) => r.data),
    enabled: !!docId,
  });
  const items = q.data?.items ?? [];
  return (
    <div className="docrail__card" data-testid="doc-history-card">
      <div className="docrail__title">Історія документа</div>
      {q.isLoading && <div style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>Завантаження…</div>}
      {!q.isLoading && items.length === 0 && (
        <div style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>Записів немає.</div>
      )}
      {latestTwo(items).map((h, i) => (
        <div key={i} className="docrail__row">
          <div>{ACTION_LABEL[h.action] ?? h.action.toLowerCase()} · {fmtDT(h.occurred_at)}</div>
          {h.user_email && <div style={{ color: 'var(--muted)' }}>{h.user_email}</div>}
        </div>
      ))}
      {items.length > 2 && (
        <button type="button" className="docrail__more" onClick={() => setOpen(true)}>
          ще ({items.length - 2}) →
        </button>
      )}
      {open && docId && (
        <HistoryModal docType={doc.type} docId={docId} onClose={() => setOpen(false)} />
      )}
    </div>
  );
}

/** Звʼязані документи: 2 останні + «ще» → наявний попап. */
function RelatedCard({ doc }: { doc: DtKtDocRef }) {
  const navigate = useNavigate();
  const q = useRelatedDocs(doc);
  const items = q.data ?? [];
  // ч.874 (зауваження власника): картка — ЗАВЖДИ (кнопку «звʼязані» з шапки
  // прибрано у railMode, тож це єдине місце, де видно ланцюг документа);
  // без звʼязків — «немає», як «Записів немає» в історії.
  return (
    <div className="docrail__card" data-testid="doc-related-card">
      <div className="docrail__title">Звʼязані документи</div>
      {q.isLoading && <div style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>Завантаження…</div>}
      {!q.isLoading && items.length === 0 && (
        <div style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>Звʼязаних документів немає.</div>
      )}
      {latestTwo(items).map((r) => {
        const route = relatedDocRoute(r);
        return (
          <div key={`${r.document_type}:${r.document_id}`} className="docrail__row">
            <button type="button" className="docrail__link" style={{ padding: 0 }}
              disabled={!route} onClick={() => route && navigate(route)}>
              <span style={{ flex: 1 }}>
                {relatedDocName(r)} № {r.number ?? '—'}
              </span>
            </button>
            <div style={{ color: 'var(--muted)' }}>
              {fmtD(r.date)} · {relationLabel(r)}
            </div>
          </div>
        );
      })}
      {/* ч.1220: «привʼязати» — для документів «Комерції», податкових накладних і оплат
          (решті кнопка не показується); попап «звʼязані» — коли в ньому є що робити:
          більше двох документів або ручний звʼязок, який можна прибрати. */}
      <div style={{ marginTop: 4, display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
        <LinkDocumentButton doc={doc} linkedKeys={relatedKeys(q.data)} />
        {(items.length > 2 || items.some((r) => r.link_kind === 'manual')) && (
          <RelatedDocsButton doc={doc} />
        )}
      </div>
    </div>
  );
}
